(() => {
  const DEBUG = (() => {
    try {
      return new URLSearchParams(window.location.search).has('sd_debug');
    } catch (e) {
      return false;
    }
  })();
  const log = (...args) => {
    if (!DEBUG) return;
    // eslint-disable-next-line no-console
    console.log('[search-discovery]', ...args);
  };

  const SELECTORS = {
    form: '[data-search-discovery-form]',
    input: '[data-search-discovery-input]',
    results: '[data-search-discovery-results]',
  };

  const MIN_CHARS = 2;
  const LIMIT = 8;
  const DEBOUNCE_MS = 120;
  const RESOURCE_TYPES = 'product,page,query'; // align with Search & Discovery predictive settings (no blog posts)

  const escapeHtml = (str) =>
    String(str).replace(/[&<>"']/g, (ch) => {
      switch (ch) {
        case '&':
          return '&amp;';
        case '<':
          return '&lt;';
        case '>':
          return '&gt;';
        case '"':
          return '&quot;';
        case "'":
          return '&#039;';
        default:
          return ch;
      }
    });

  const getSuggestUrl = (q) => {
    const url = new URL('/search/suggest.json', window.location.origin);
    url.searchParams.set('q', q);
    url.searchParams.set('resources[type]', RESOURCE_TYPES);
    url.searchParams.set('resources[limit]', String(LIMIT));
    url.searchParams.set('resources[options][unavailable_products]', 'show');
    url.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,vendor');
    return url.toString();
  };

  // Fallback for older themes: uses templates/search.json.liquid (view=json)
  const getLegacyJsonUrl = (q) => {
    const url = new URL('/search', window.location.origin);
    url.searchParams.set('q', q + '*');
    url.searchParams.set('view', 'json');
    return url.toString();
  };

  const closeResults = (resultsEl) => {
    if (!resultsEl) return;
    resultsEl.hidden = true;
    resultsEl.innerHTML = '';
    resultsEl.style.removeProperty('display');
    resultsEl.style.removeProperty('opacity');
  };

  const renderLegacy = (resultsEl, q, data) => {
    const results = Array.isArray(data?.results) ? data.results : [];
    if (!results.length) {
      resultsEl.innerHTML = `<div class="predictive-results__empty">No results</div>`;
      resultsEl.hidden = false;
      return;
    }

    const items = results
      .slice(0, LIMIT)
      .map((r) => {
        const title = escapeHtml(r.title);
        const img = r.thumbnail
          ? `<img class="predictive-results__img" src="${r.thumbnail}" alt="${title}" loading="lazy">`
          : '';
        return `<li class="predictive-results__item">
          <a class="predictive-results__link" href="${r.url}">
            ${img}
            <span class="predictive-results__title">${title}</span>
          </a>
        </li>`;
      })
      .join('');

    const viewAllUrl = `/search?q=${encodeURIComponent(q)}&options[prefix]=last`;
    resultsEl.innerHTML = `
      <div class="predictive-results__panel" role="listbox">
        <div class="predictive-results__group">
          <div class="predictive-results__heading">Results</div>
          <ul class="predictive-results__list">${items}</ul>
        </div>
        <div class="predictive-results__footer">
          <a class="predictive-results__view-all" href="${viewAllUrl}">View all results</a>
        </div>
      </div>
    `;
    resultsEl.hidden = false;
    resultsEl.style.display = 'block';
    resultsEl.style.opacity = '1';
  };

  const render = (resultsEl, q, data) => {
    const resources = data?.resources?.results || {};
    const products = Array.isArray(resources.products) ? resources.products : [];
    const pages = Array.isArray(resources.pages) ? resources.pages : [];
    const queries = Array.isArray(resources.queries) ? resources.queries : [];

    const hasAny = products.length || pages.length || queries.length;
    if (!hasAny) {
      resultsEl.innerHTML = `<div class="predictive-results__empty">No results</div>`;
      resultsEl.hidden = false;
      return;
    }

    const formatMoney = (val) => {
      if (typeof val === 'number' && !Number.isNaN(val)) {
        // suggest.json uses cents in many themes
        return `$${(val / 100).toFixed(0)}`;
      }
      if (typeof val === 'string') {
        const s = val.trim();
        if (s) return s.startsWith('$') ? s : `$${s}`;
      }
      return '';
    };

    const productItems = products
      .map((p) => {
        const title = escapeHtml(p.title);
        const url = p.url;
        const img = p.image ? `<img class="predictive-results__img" src="${p.image}" alt="${title}" loading="lazy">` : '';
        const priceVal = formatMoney(p.price ?? p.price_min ?? p.price_max ?? '');
        const price = priceVal ? `<span class="predictive-results__meta">${escapeHtml(priceVal)}</span>` : '';
        return `<li class="predictive-results__item">
          <a class="predictive-results__link" href="${url}">
            ${img}
            <span class="predictive-results__title">${title}</span>
            ${price}
          </a>
        </li>`;
      })
      .join('');

    const pageItems = pages
      .map((p) => {
        const title = escapeHtml(p.title);
        return `<li class="predictive-results__item">
          <a class="predictive-results__link" href="${p.url}">
            <span class="predictive-results__title">${title}</span>
          </a>
        </li>`;
      })
      .join('');

    const queryItems = queries
      .map((qItem) => {
        // suggest.json "queries" items vary; support string or { text }
        const text = typeof qItem === 'string' ? qItem : qItem?.text;
        if (!text) return '';
        const safe = escapeHtml(text);
        const href = `/search?q=${encodeURIComponent(text)}&options[prefix]=last`;
        return `<li class="predictive-results__item">
          <a class="predictive-results__link" href="${href}">
            <span class="predictive-results__title">${safe}</span>
          </a>
        </li>`;
      })
      .filter(Boolean)
      .join('');

    const viewAllUrl = `/search?q=${encodeURIComponent(q)}&options[prefix]=last`;

    resultsEl.innerHTML = `
      <div class="predictive-results__panel" role="listbox">
        ${products.length ? `<div class="predictive-results__group">
          <div class="predictive-results__heading">Products</div>
          <ul class="predictive-results__list">${productItems}</ul>
        </div>` : ''}
        ${pages.length ? `<div class="predictive-results__group">
          <div class="predictive-results__heading">Pages</div>
          <ul class="predictive-results__list">${pageItems}</ul>
        </div>` : ''}
        ${queryItems ? `<div class="predictive-results__group">
          <div class="predictive-results__heading">Suggestions</div>
          <ul class="predictive-results__list">${queryItems}</ul>
        </div>` : ''}
        <div class="predictive-results__footer">
          <a class="predictive-results__view-all" href="${viewAllUrl}">View all results</a>
        </div>
      </div>
    `;
    resultsEl.hidden = false;
    resultsEl.style.display = 'block';
    resultsEl.style.opacity = '1';
  };

  const initForm = (form) => {
    // Support both the new data-attrs and the theme's legacy predictiveSearch selectors
    const input =
      form.querySelector(SELECTORS.input) ||
      form.querySelector('input#q') ||
      form.querySelector('input[name="q"]');
    const resultsEl =
      form.querySelector(SELECTORS.results) ||
      form.querySelector('.predictive-results') ||
      null;
    if (!input || !resultsEl) return;
    log('initForm', { form, input, resultsEl });

    let abort = null;
    let t = null;

    const run = async () => {
      const q = input.value.trim();
      log('run', { q });
      if (q.length < MIN_CHARS) {
        closeResults(resultsEl);
        return;
      }

      if (abort) abort.abort();
      abort = new AbortController();

      try {
        const url = getSuggestUrl(q);
        log('fetch suggest', url);
        const res = await fetch(url, {
          signal: abort.signal,
          headers: { Accept: 'application/json' },
        });
        if (!res.ok) throw new Error(`suggest status ${res.status}`);
        const data = await res.json();
        log('suggest ok', data);
        render(resultsEl, q, data);
      } catch (e) {
        if (e?.name === 'AbortError') return;
        log('suggest failed', e);
        // Fallback to legacy json view if suggest endpoint is blocked/disabled
        try {
          const url2 = getLegacyJsonUrl(q);
          log('fetch legacy', url2);
          const res2 = await fetch(url2, { signal: abort.signal, headers: { Accept: 'application/json' } });
          if (!res2.ok) throw new Error(`legacy status ${res2.status}`);
          const data2 = await res2.json();
          log('legacy ok', data2);
          renderLegacy(resultsEl, q, data2);
        } catch (e2) {
          if (e2?.name === 'AbortError') return;
          log('legacy failed', e2);
          closeResults(resultsEl);
        }
      }
    };

    const schedule = () => {
      clearTimeout(t);
      t = setTimeout(run, DEBOUNCE_MS);
    };
    input.addEventListener('input', schedule);
    input.addEventListener('keyup', schedule);

    input.addEventListener('focus', () => {
      if (resultsEl.innerHTML.trim()) resultsEl.hidden = false;
    });

    document.addEventListener('click', (e) => {
      if (!form.contains(e.target)) closeResults(resultsEl);
    });

    form.addEventListener('submit', () => closeResults(resultsEl));
  };

  const initAll = () => {
    // New + legacy forms
    const forms = new Set();
    document.querySelectorAll(SELECTORS.form).forEach((f) => forms.add(f));
    document.querySelectorAll('form.search__form, form.search-form').forEach((f) => forms.add(f));
    log('initAll forms', forms.size);
    forms.forEach(initForm);
  };

  // Delegated input handling to survive DOM replacements
  const stateByInput = new WeakMap();
  const getState = (input) => {
    let s = stateByInput.get(input);
    if (!s) {
      s = { t: null, abort: null };
      stateByInput.set(input, s);
    }
    return s;
  };
  const findResultsEl = (input) => {
    const form = input.closest('form');
    if (!form) return null;
    return (
      form.querySelector(SELECTORS.results) ||
      form.querySelector('.predictive-results') ||
      null
    );
  };
  const runForInput = async (input) => {
    const resultsEl = findResultsEl(input);
    if (!resultsEl) return;

    const q = input.value.trim();
    log('run(delegated)', { q, input });
    if (q.length < MIN_CHARS) {
      closeResults(resultsEl);
      return;
    }

    const s = getState(input);
    if (s.abort) s.abort.abort();
    s.abort = new AbortController();

    try {
      const url = getSuggestUrl(q);
      log('fetch suggest(delegated)', url);
      const res = await fetch(url, { signal: s.abort.signal, headers: { Accept: 'application/json' } });
      if (!res.ok) throw new Error(`suggest status ${res.status}`);
      const data = await res.json();
      log('suggest ok(delegated)', data);
      render(resultsEl, q, data);
    } catch (e) {
      if (e?.name === 'AbortError') return;
      log('suggest failed(delegated)', e);
      try {
        const url2 = getLegacyJsonUrl(q);
        log('fetch legacy(delegated)', url2);
        const res2 = await fetch(url2, { signal: s.abort.signal, headers: { Accept: 'application/json' } });
        if (!res2.ok) throw new Error(`legacy status ${res2.status}`);
        const data2 = await res2.json();
        log('legacy ok(delegated)', data2);
        renderLegacy(resultsEl, q, data2);
      } catch (e2) {
        if (e2?.name === 'AbortError') return;
        log('legacy failed(delegated)', e2);
        closeResults(resultsEl);
      }
    }
  };
  const scheduleForInput = (input) => {
    const s = getState(input);
    clearTimeout(s.t);
    s.t = setTimeout(() => runForInput(input), DEBOUNCE_MS);
  };

  document.addEventListener(
    'input',
    (e) => {
      const el = e.target;
      if (!(el instanceof HTMLInputElement)) return;
      if (el.matches(SELECTORS.input) || el.matches('form.search__form input[name="q"], form.search-form input[name="q"], input#q')) {
        scheduleForInput(el);
      }
    },
    true
  );
  document.addEventListener(
    'keyup',
    (e) => {
      const el = e.target;
      if (!(el instanceof HTMLInputElement)) return;
      if (el.matches(SELECTORS.input) || el.matches('form.search__form input[name="q"], form.search-form input[name="q"], input#q')) {
        scheduleForInput(el);
      }
    },
    true
  );

  // Re-init when header scripts replace nodes
  let _moTimer = null;
  let _lastFormCount = 0;
  const mo = new MutationObserver((mutations) => {
    // Ignore mutations caused by us rendering results
    for (const m of mutations) {
      for (const n of m.addedNodes) {
        if (n.nodeType === 1 && (n.matches?.('.predictive-results__panel') || n.closest?.('.predictive-results'))) {
          return;
        }
      }
    }
    if (_moTimer) return;
    _moTimer = setTimeout(() => {
      _moTimer = null;
      const count =
        document.querySelectorAll(SELECTORS.form).length +
        document.querySelectorAll('form.search__form, form.search-form').length;
      if (count !== _lastFormCount) {
        _lastFormCount = count;
        initAll();
      }
    }, 200);
  });
  if (document.body) mo.observe(document.body, { childList: true, subtree: true });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Small debug helpers
  if (DEBUG) {
    window.__sdDebug = {
      initAll,
      suggest: (q) => fetch(getSuggestUrl(q)).then((r) => r.json()),
      legacy: (q) => fetch(getLegacyJsonUrl(q)).then((r) => r.json()),
    };
    log('debug enabled: add ?sd_debug=1');
  }
})();

